<template>
  <v-card class="pa-6 bulk-import">
    <v-card-title class="text-uppercase justify-space-between">
      Carga masiva de servicios
      <v-btn icon :disabled="submitting" @click="closeDialog"><v-icon>mdi-close</v-icon></v-btn>
    </v-card-title>
    <hr class="my-3" />
    <v-card-text>
      <div class="pw-label mb-2">Planilla</div>
      <div class="text-body-2 mb-3">
        Columnas: <strong>Servicio</strong>, <strong>Precio final</strong> y
        <strong>Cobertura Pawer a la que corresponde</strong>.
      </div>
      <v-row class="my-1">
        <v-col cols="12" md="5">
          <v-file-input
            label="Subir .csv o .xlsx"
            accept=".csv,.xlsx"
            prepend-icon="mdi-file-upload"
            :disabled="submitting"
            outlined
            dense
            hide-details="auto"
            @change="onFileSelected"
          ></v-file-input>
        </v-col>
        <v-col cols="12" md="7">
          <v-textarea
            v-model="pastedText"
            label="O pega las filas copiadas desde Google Sheets"
            rows="2"
            :disabled="submitting"
            outlined
            dense
            hide-details="auto"
          ></v-textarea>
          <div class="d-flex justify-end mt-2">
            <v-btn small text color="primary" :disabled="!pastedText.trim() || submitting" @click="readPasted">
              Leer filas pegadas
            </v-btn>
          </div>
        </v-col>
      </v-row>
      <v-alert v-if="parseError" type="error" dense text class="mt-2">{{ parseError }}</v-alert>

      <template v-if="rows.length">
        <hr class="my-4" />
        <div class="pw-label mb-2">Servicio Pawer por cobertura</div>
        <v-row v-for="coverage in coverages" :key="coverage" class="my-0" align="center">
          <v-col cols="12" md="4" class="py-1">
            <div class="pw-value">{{ coverage }}</div>
            <div class="text-caption coverage-count">{{ rowCountByCoverage[coverage] }} filas</div>
          </v-col>
          <v-col cols="12" md="8" class="py-1">
            <v-autocomplete
              v-model="assignments[coverage]"
              :items="catalogServices"
              :loading="loadingCatalogServices"
              :disabled="submitting"
              item-text="title"
              item-value="id"
              label="Servicio del catálogo Pawer"
              outlined
              dense
              hide-details="auto"
            ></v-autocomplete>
          </v-col>
        </v-row>

        <hr class="my-4" />
        <div class="pw-label mb-2">Vista previa</div>
        <v-alert v-if="submitError" type="error" dense text>{{ submitError }}</v-alert>
        <v-data-table
          :headers="headers"
          :items="rows"
          item-key="id"
          :items-per-page="50"
          dense
          class="preview-table"
        >
          <template v-slot:item.title="{ item }">
            <v-text-field
              v-model="item.title"
              :disabled="submitting"
              dense
              hide-details
              single-line
              @input="onRowEdited(item)"
            ></v-text-field>
          </template>
          <template v-slot:item.priceText="{ item }">
            <v-text-field
              v-model="item.priceText"
              :disabled="submitting"
              :suffix="parsedPrice(item) | clp"
              dense
              hide-details
              single-line
              @input="onRowEdited(item)"
            ></v-text-field>
          </template>
          <template v-slot:item.pawerService="{ item }">
            <span v-if="serviceTitle(item)">{{ serviceTitle(item) }}</span>
            <span v-else class="coverage-count">Sin asignar</span>
          </template>
          <template v-slot:item.errors="{ item }">
            <v-chip
              v-for="message in rowErrorMessages(item)"
              :key="message"
              x-small
              outlined
              color="error"
              class="mr-1 my-1"
            >
              {{ message }}
            </v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small color="error" v-bind="attrs" v-on="on" :disabled="submitting" @click="removeRow(item)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Quitar fila</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </template>
    </v-card-text>
    <hr class="my-3" />
    <v-card-actions>
      <div class="text-body-2 summary">
        <template v-if="rows.length">{{ summary }}</template>
      </div>
      <v-spacer></v-spacer>
      <v-btn text color="primary" rounded :disabled="submitting" @click="closeDialog">Cancelar</v-btn>
      <v-btn color="primary" rounded :disabled="!canConfirm" :loading="submitting" @click="confirm">
        Crear {{ rows.length || '' }} servicios
      </v-btn>
    </v-card-actions>

    <v-snackbar v-model="showSnackbar" :timeout="5000" :color="snackbarColor">
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false">Cerrar</v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import ClinicsService from '../services/clinics.service';
import ServicesService from '../services/services.service';
import { utilsMixin } from '../mixins/utilsMixin';
import {
  readImportFile,
  readImportText,
  parsePrice,
  groupCoverages,
  buildBulkItems,
  validateRows,
  mapServerErrors,
  errorMessage
} from '../utils/clinic-services-import.utils';

// Límite del endpoint bulk (pawer-backend spec 105).
const MAX_ITEMS = 500;

// Errores que no son por fila: en ninguno se creó nada (contracts/bulk-catalog-services.md).
const SUBMIT_ERROR_MESSAGES = {
  404: 'La clínica no existe.',
  409: 'Otra carga creó un servicio con el mismo título al mismo tiempo. No se creó nada; intenta de nuevo.',
  422: 'La carga fue rechazada; no se creó ningún servicio. Revisa las filas marcadas.'
};
const GENERIC_SUBMIT_ERROR = 'No se pudo hacer la carga. No se creó ningún servicio.';

export default {
  name: 'ClinicServicesBulkImportModal',

  mixins: [utilsMixin],

  props: {
    clinicId: { type: String, required: true }
  },

  data() {
    return {
      rows: [],
      assignments: {},
      catalogServices: [],
      loadingCatalogServices: false,
      pastedText: '',
      parseError: '',
      submitting: false,
      submitError: '',
      headers: [
        { text: 'Fila', value: 'sourceLine', width: 60 },
        { text: 'Título', value: 'title' },
        { text: 'Precio', value: 'priceText', width: 170 },
        { text: 'Cobertura', value: 'coverage' },
        { text: 'Servicio Pawer', value: 'pawerService', sortable: false },
        { text: 'Errores', value: 'errors', sortable: false },
        { text: '', value: 'actions', sortable: false, width: 48 }
      ],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },

  computed: {
    coverages() {
      return groupCoverages(this.rows);
    },
    rowCountByCoverage() {
      return this.rows.reduce((count, row) => {
        count[row.coverage] = (count[row.coverage] || 0) + 1;
        return count;
      }, {});
    },
    clientErrors() {
      return validateRows(this.rows);
    },
    rowsWithClientErrors() {
      return this.rows.filter(row => this.clientErrors[row.id].length).length;
    },
    unassignedCoverages() {
      return this.coverages.filter(coverage => !this.assignments[coverage]).length;
    },
    catalogTitleById() {
      return new Map(this.catalogServices.map(service => [service.id, service.title]));
    },
    canConfirm() {
      return (
        this.rows.length > 0 &&
        this.rows.length <= MAX_ITEMS &&
        this.unassignedCoverages === 0 &&
        this.rowsWithClientErrors === 0 &&
        !this.submitting
      );
    },
    summary() {
      const parts = [`${this.rows.length} filas`];
      if (this.rowsWithClientErrors) parts.push(`${this.rowsWithClientErrors} con error`);
      if (this.unassignedCoverages) parts.push(`${this.unassignedCoverages} coberturas sin asignar`);
      if (this.rows.length > MAX_ITEMS) parts.push(`máximo ${MAX_ITEMS} por carga`);
      return parts.join(' · ');
    }
  },

  mounted() {
    this.fetchCatalogServices();
  },

  methods: {
    async fetchCatalogServices() {
      this.loadingCatalogServices = true;
      try {
        const res = await ServicesService.getAllServices();
        this.catalogServices = res.data;
      } catch (error) {
        this.showErrorSnackbar('No se pudo cargar el catálogo de servicios Pawer.');
      }
      this.loadingCatalogServices = false;
    },

    async onFileSelected(file) {
      if (!file) return;
      try {
        this.loadRows(await readImportFile(file));
      } catch (error) {
        this.setParseError('No se pudo leer el archivo. Verifica que sea un .csv o .xlsx válido.');
      }
    },

    readPasted() {
      try {
        this.loadRows(readImportText(this.pastedText));
      } catch (error) {
        this.setParseError('No se pudieron leer las filas pegadas.');
      }
    },

    setParseError(message) {
      this.rows = [];
      this.parseError = message;
    },

    // Una planilla nueva reemplaza la vista previa, pero conserva la asignación de las
    // coberturas que siguen apareciendo.
    loadRows(rows) {
      this.submitError = '';
      if (!rows.length) {
        this.setParseError('No se encontraron filas con datos.');
        return;
      }
      this.parseError = '';
      this.rows = rows;
      this.assignments = groupCoverages(rows).reduce((assignments, coverage) => {
        assignments[coverage] = this.assignments[coverage] || null;
        return assignments;
      }, {});
    },

    onRowEdited(row) {
      if (row.serverErrors.length) row.serverErrors = [];
    },

    removeRow(row) {
      this.rows = this.rows.filter(r => r.id !== row.id);
    },

    parsedPrice(row) {
      return parsePrice(row.priceText).value ?? '';
    },

    serviceTitle(row) {
      return this.catalogTitleById.get(this.assignments[row.coverage]) || '';
    },

    rowErrorMessages(row) {
      const codes = [...this.clientErrors[row.id], ...row.serverErrors];
      return [...new Set(codes.map(errorMessage))];
    },

    async confirm() {
      if (!this.canConfirm) return;

      const sentRowIds = this.rows.map(row => row.id);
      this.submitting = true;
      this.submitError = '';
      const res = await ClinicsService.createCatalogServicesBulk(
        this.clinicId,
        buildBulkItems(this.rows, this.assignments)
      );
      this.submitting = false;

      if (res.status === 'ok') {
        this.$emit('imported', res.data.length);
        this.closeDialog();
        return;
      }

      const errorsByRow = mapServerErrors(res.errors, sentRowIds);
      this.rows.forEach(row => {
        row.serverErrors = errorsByRow[row.id] || [];
      });
      const hasRowErrors = Object.keys(errorsByRow).length > 0;
      this.submitError =
        res.httpCode === 422 && !hasRowErrors
          ? GENERIC_SUBMIT_ERROR
          : SUBMIT_ERROR_MESSAGES[res.httpCode] || GENERIC_SUBMIT_ERROR;
    },

    closeDialog() {
      this.$emit('close-dialog');
    }
  }
};
</script>

<style scoped>
.coverage-count {
  color: var(--pw-text-muted);
}

.summary {
  color: var(--pw-text-muted);
}

.preview-table {
  border: 1px solid var(--pw-border);
  border-radius: var(--pw-radius-card);
}
</style>
